import { Ban, Check, PauseCircle, Store as StoreIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Store, StoreStatus } from '@/features/stores';
import { Button, EmptyState, ErrorState, SearchInput, Spinner, StatusPill } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';

const STATUS_TONE: Record<StoreStatus, 'success' | 'warning' | 'danger'> = {
  ACTIVE: 'success',
  TEMPORARILY_CLOSED: 'warning',
  PERMANENTLY_CLOSED: 'danger',
};

const STATUS_ICON = {
  ACTIVE: Check,
  TEMPORARILY_CLOSED: PauseCircle,
  PERMANENTLY_CLOSED: Ban,
} as const;

export interface OfflineStorePickerProps {
  stores: Store[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selectedStoreId: string | null;
  canGoBack: boolean;
  onBack: () => void;
  onSelect: (storeId: string) => void;
}

export function OfflineStorePicker({
  stores,
  isLoading,
  isError,
  onRetry,
  selectedStoreId,
  canGoBack,
  onBack,
  onSelect,
}: OfflineStorePickerProps) {
  const { t } = useTranslation('offlineOrders');
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();

  const visible = useMemo(() => {
    const filtered = stores.filter((store) => {
      if (!normalized) return true;
      const haystack = `${store.name} ${store.storeCode} ${store.city}`.toLowerCase();
      return haystack.includes(normalized);
    });
    return [...filtered].sort((a, b) => {
      if (a.status === b.status) return a.name.localeCompare(b.name);
      if (a.status === 'ACTIVE') return -1;
      if (b.status === 'ACTIVE') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [normalized, stores]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label={t('store.pickTitle')} size={28} />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={t('store.emptyDescription')} onRetry={onRetry} />;
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-fg">{t('store.pickTitle')}</h2>
          <p className="mt-1 text-sm text-fg-muted">{t('store.pickDescription')}</p>
        </div>
        {canGoBack ? (
          <Button type="button" variant="secondary" onClick={onBack}>
            {t('store.back')}
          </Button>
        ) : null}
      </div>

      <SearchInput
        id="offline-store-search"
        label={t('store.searchLabel')}
        placeholder={t('store.searchPlaceholder')}
        value={query}
        onValueChange={setQuery}
      />

      {visible.length === 0 ? (
        <EmptyState
          icon={StoreIcon}
          title={t('store.emptyTitle')}
          description={t('store.emptyDescription')}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {visible.map((store) => {
            const closed = store.status === 'PERMANENTLY_CLOSED';
            const selected = store.id === selectedStoreId;
            const StatusIcon = STATUS_ICON[store.status];
            return (
              <li key={store.id}>
                <button
                  type="button"
                  disabled={closed}
                  onClick={() => onSelect(store.id)}
                  className={cn(
                    'flex w-full flex-col gap-2 rounded-md border p-4 text-left',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    selected
                      ? 'border-primary bg-primary-subtle'
                      : 'border-border bg-surface hover:bg-surface-muted',
                  )}
                >
                  <span className="text-base font-semibold text-fg">{store.name}</span>
                  <span className="text-sm text-fg-muted">
                    {store.storeCode}
                    {store.city ? ` · ${store.city}` : ''}
                  </span>
                  <StatusPill tone={STATUS_TONE[store.status]} icon={StatusIcon}>
                    {t(`store.status.${store.status}`)}
                  </StatusPill>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
